import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, ChevronDown, ChevronUp, Users, Zap, MessageSquare, Clock, Copy, Check } from "lucide-react";
import type { RoomAnalytics } from "@/lib/room-state";

interface AnalyticsPanelProps { analytics: RoomAnalytics | null; currentViewers: number; }
function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000); const m = Math.floor(s / 60); const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`; if (m > 0) return `${m}m ${s % 60}s`; return `${s}s`;
}
export default function AnalyticsPanel({ analytics, currentViewers }: AnalyticsPanelProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!analytics) return null;
  const a = analytics as NonNullable<typeof analytics>;
  function exportReport() {
    const report = ["SyncScreen — Room Analytics Report", `Generated: ${new Date().toISOString()}`, `Room age: ${formatDuration(Date.now() - a.createdAt)}`, "", `Current viewers:  ${currentViewers}`, `Peak viewers:     ${a.peakViewers}`, `Total reactions:  ${a.totalReactions}`, `Total messages:   ${a.totalMessages}`, `Timeline comments: ${a.totalComments}`].join("\n");
    navigator.clipboard?.writeText(report).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }
  const uptime = Date.now() - a.createdAt;
  const stats = [
    { icon: Users, label: "Current", value: currentViewers, tint: "text-[#89CFF0]", bg: "from-white/[0.05] to-transparent" },
    { icon: BarChart3, label: "Peak", value: a.peakViewers, tint: "text-primary", bg: "from-white/[0.06] to-transparent" },
    { icon: Zap, label: "Reactions", value: a.totalReactions, tint: "text-muted-foreground", bg: "from-white/[0.05] to-transparent" },
    { icon: MessageSquare, label: "Messages", value: a.totalMessages, tint: "text-muted-foreground", bg: "from-white/[0.05] to-transparent" },
  ];
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent pointer-events-none" />
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors relative">
 <span className="w-7 h-7 flex items-center justify-center"><BarChart3 className="w-3.5 h-3.5" /></span>
        <span className="font-semibold tracking-tight">Room analytics</span>
 <span className="ml-auto w-7 h-7 flex items-center justify-center">{open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22, ease: [0.16,1,0.3,1] }} className="overflow-hidden relative">
            <div className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-2.5">
                {stats.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-secondary border border-white/[0.04] rounded-lg p-3 flex items-center gap-2.5 relative overflow-hidden group">
                      <div className={`absolute inset-0 bg-gradient-to-br ${s.bg} opacity-60`} />
 <span className="w-9 h-9 flex items-center justify-center shrink-0 relative"><Icon className={`w-4 h-4 ${s.tint}`} /></span>
                      <div className="min-w-0 relative"><div className="text-sm font-bold tabular-nums">{s.value}</div><div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">{s.label}</div></div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground glass-subtle rounded-xl px-3 py-2">
                <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> Room age: {formatDuration(uptime)}</span>
                <button onClick={exportReport} className="flex items-center gap-1.5 glass px-2.5 py-1 rounded-full hover:glass-strong transition-colors font-medium">
                  {copied ? <Check className="w-3 h-3 text-[#89CFF0]" /> : <Copy className="w-3 h-3" />}{copied ? "Copied" : "Export"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
